import React, { useState, useEffect } from 'react';
import {
  MailIcon, PhoneIcon, UserIcon, ClockIcon, FileTextIcon, XIcon, BookOpenIcon,
  CalendarDays as CalendarIcon,
} from 'lucide-react';
import './StudentDetailModal.css';

/* --- Funções Auxiliares --- */
const getStatusClassFromValue = (value, type) => {
  if (!value || value === "N/A") return 'default';
  let numericValue;
  if (type === 'frequencia' || type === 'freqDisciplina') {
    numericValue = parseFloat(String(value).replace('%', ''));
    if (isNaN(numericValue)) return 'default';
    if (numericValue === 100) return 'perfect';
    if (numericValue >= 80) return 'positive';
    if (numericValue >= 75) return 'warning';
    return 'danger';
  } else if (type === 'desempenho' || type === 'notaDisciplina') {
    numericValue = parseFloat(String(value));
    if (isNaN(numericValue)) return 'default';
    if (numericValue === 10) return 'perfect';
    if (numericValue >= 8) return 'positive';
    if (numericValue >= 7) return 'warning';
    return 'danger';
  }
  return 'default';
};

const getLatestPeriod = (disciplinas) => {
  if (!disciplinas || disciplinas.length === 0) {
    return null;
  }
  // Filtra para garantir que 'periodo' existe e está no formato esperado "YYYY.S"
  const validPeriods = disciplinas
    .map(d => d.periodo)
    .filter(p => typeof p === 'string' && /^\d{4}\.\d$/.test(p));

  if (validPeriods.length === 0) return null;

  // Ordena os períodos: primeiro por ano (descendente), depois por semestre (descendente)
  validPeriods.sort((a, b) => {
    const [yearA, semA] = a.split('.').map(Number);
    const [yearB, semB] = b.split('.').map(Number);
    if (yearB !== yearA) {
      return yearB - yearA;
    }
    return semB - semA;
  });
  return validPeriods[0];
};


/* --- Componente Principal --- */
const StudentDetailModal = ({ isOpen, onClose, student }) => {
  /* --- Estados --- */
  const [activeTab, setActiveTab] = useState('disciplinas');
  const [currentAnotacao, setCurrentAnotacao] = useState('');

  /* --- Efeitos --- */
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
      setCurrentAnotacao(student?.anotacoesPrivadas || '');
      setActiveTab('disciplinas');
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose, student]);

  /* --- Lógica de Dados para Renderização --- */
  const currentPeriod = isOpen && student ? getLatestPeriod(student.disciplinas) : null;
  const disciplinasDoPeriodoAtual = isOpen && student && currentPeriod && student.disciplinas
    ? student.disciplinas.filter(d => d.periodo === currentPeriod)
    : [];

  /* --- Manipuladores (Handlers) --- */
  const handleSaveAnotacao = () => {
    console.log("Anotação salva para", student.name, ":", currentAnotacao);
    alert("Anotações salvas (ver console)!");
  };

  // Cláusula de guarda
  if (!isOpen || !student) {
    return null;
  }

  const tabOptions = [
    { id: 'disciplinas', label: 'Disciplinas' },
    { id: 'frequencia', label: 'Frequência' },
    { id: 'anotacoes', label: 'Anotações' },
  ];

  /* --- Renderização --- */
  return (
    <div className={`stb-modal-overlay ${isOpen ? 'stb-modal-open' : ''}`} onClick={onClose}>
      <div className="stb-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="stb-modal-header">
          <h2 id="student-modal-title" className="stb-modal-title">Detalhes do Aluno</h2>
          <button onClick={onClose} className="stb-modal-close-button" aria-label="Fechar">
            <XIcon className="stb-icon-xs" />
          </button>
        </div>

        <div className="stb-modal-body">
          <div className="stb-student-profile-section">
            <span className="stb-modal-avatar-wrapper">
              <img className="stb-modal-avatar-img" alt={student.name} src={student.imgSrcModal || student.imgSrc} />
            </span>
            <div className="stb-student-info-main">
              <h3 className="stb-student-name-modal">{student.name}</h3>
              <div className="stb-student-contact-info">
                <div className="stb-contact-item">
                  <MailIcon className="stb-icon-xxs stb-muted-icon" />
                  <span>{student.email}</span>
                </div>
                <div className="stb-contact-item">
                  <PhoneIcon className="stb-icon-xxs stb-muted-icon" />
                  <span>{student.telefone || "Não informado"}</span>
                </div>
              </div>
              <div className="stb-student-academic-info">
                <div className="stb-academic-item">
                  <UserIcon className="stb-icon-xxs stb-muted-icon" />
                  <span>Matrícula: {student.matricula}</span>
                </div>
                <div className="stb-academic-item">
                  <BookOpenIcon className="stb-icon-xxs stb-muted-icon" />
                  <span>{student.curso}</span>
                </div>
              </div>
            </div>
            <div className="stb-student-performance-badges">
              <div className={`stb-status-badge stb-badge-${getStatusClassFromValue(student.frequencia, 'frequencia')} stb-modal-badge`}>
                <ClockIcon className="stb-icon-tiny" />
                <span>Frequência: {student.frequencia}</span>
              </div>
              <div className={`stb-status-badge stb-badge-${getStatusClassFromValue(student.desempenho, 'desempenho')} stb-modal-badge`}>
                <FileTextIcon className="stb-icon-tiny" />
                <span>Desempenho: {student.desempenho}</span>
              </div>
            </div>
          </div>

          <div className="stb-tabs-container">
            <div className="stb-tab-list" role="tablist" aria-labelledby="student-modal-title">
              {tabOptions.map(tab => (
                <button
                  key={tab.id}
                  id={`stb-tab-${tab.id}`}
                  role="tab"
                  type="button"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`stb-tabpanel-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`stb-tab-button ${activeTab === tab.id ? 'stb-tab-active' : ''}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="stb-tab-content-area">
              {/* Aba de Disciplinas */}
              {activeTab === 'disciplinas' && (
                <div
                  id="stb-tabpanel-disciplinas"
                  role="tabpanel"
                  aria-labelledby="stb-tab-disciplinas"
                  className="stb-tab-panel"
                >
                  <div className="stb-disciplinas-content">
                    {currentPeriod && (
                      <p className="stb-current-period-indicator">
                        Exibindo disciplinas do período: <strong>{currentPeriod}</strong>
                      </p>
                    )}
                    {disciplinasDoPeriodoAtual.length > 0 ? (
                      <div className="stb-disciplinas-table-wrapper">
                        <div className="stb-disciplinas-header">
                          <div>Disciplina</div>
                          <div className="stb-text-center">Nota</div>
                          <div className="stb-text-center">Frequência</div>
                        </div>
                        {disciplinasDoPeriodoAtual.map(d => (
                          <div key={d.id || d.nome} className="stb-disciplinas-row">
                            <div>{d.nome}</div>
                            <div className="stb-text-center">
                              <span className={`stb-status-badge stb-badge-xs stb-badge-${getStatusClassFromValue(d.nota, 'notaDisciplina')}`}>{d.nota}</span>
                            </div>
                            <div className="stb-text-center">
                              <span className={`stb-status-badge stb-badge-xs stb-badge-${getStatusClassFromValue(d.freq, 'freqDisciplina')}`}>{d.freq}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>
                        {currentPeriod
                          ? `Não há disciplinas cadastradas para este aluno no período ${currentPeriod}.`
                          : "Não há disciplinas ou períodos definidos para este aluno."}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Aba de Frequência */}
              {activeTab === 'frequencia' && (
                <div
                  id="stb-tabpanel-frequencia"
                  role="tabpanel"
                  aria-labelledby="stb-tab-frequencia"
                  className="stb-tab-panel"
                >
                  <div className="stb-frequencia-content">
                    {(student.frequenciaDetalhada && student.frequenciaDetalhada.length > 0) ? (
                      <div className="stb-frequencia-table-wrapper">
                        <div className="stb-frequencia-header">
                          <div>Data</div>
                          <div>Disciplina</div>
                          <div className="stb-text-center">Status</div>
                        </div>
                        {student.frequenciaDetalhada.map(record => (
                          <div key={record.id || `${record.data}-${record.disciplinaNome}`} className="stb-frequencia-row">
                            <div className="stb-frequencia-data-cell">
                              <CalendarIcon className="stb-icon-xs stb-muted-icon" />
                              <span>{record.data}</span>
                            </div>
                            <div>{record.disciplinaNome}</div>
                            <div className="stb-text-center">
                              <span
                                className={`stb-status-badge stb-badge-xs ${record.status === 'Presente' ? 'stb-badge-presente' : 'stb-badge-ausente'}`}
                              >
                                <span>{record.status}</span>
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <p>Não há registros de frequência detalhada para este aluno.</p>}
                  </div>
                </div>
              )}

              {/* Aba de Anotações */}
              {activeTab === 'anotacoes' && (
                <div
                  id="stb-tabpanel-anotacoes"
                  role="tabpanel"
                  aria-labelledby="stb-tab-anotacoes"
                  className="stb-tab-panel"
                >
                  <div className="stb-anotacoes-content">
                    <div className="stb-anotacoes-card">
                      <div className="stb-anotacoes-card-header">
                        <h3 className="stb-anotacoes-title">Anotações Privadas</h3>
                        <p className="stb-anotacoes-description">
                          Adicione observações sobre o aluno que apenas você poderá ver.
                        </p>
                      </div>
                      <div className="stb-anotacoes-card-body">
                        <textarea
                          className="stb-textarea"
                          placeholder="Adicione suas anotações aqui..."
                          value={currentAnotacao}
                          onChange={(e) => setCurrentAnotacao(e.target.value)}
                          rows={6}
                        />
                        <div className="stb-anotacoes-card-footer">
                          <button
                            onClick={handleSaveAnotacao}
                            className="stb-button stb-button-primary stb-save-anotacoes-button"
                          >
                            Salvar Anotações
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDetailModal;